import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ThreeDots } from 'react-loader-spinner';
import axiosInstance from "../api/axiosInstance";
import API_ENDPOINTS from "../api/endpoints";
import { toast } from 'react-toastify';
import { IoChevronBackOutline } from "react-icons/io5";
import CardSelector from '../components/Card/CardSelector';
import { GrNotes } from "react-icons/gr";

function Flop() {
    const { id } = useParams();
    const actionsEndRef = useRef(null);
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modalOption, setModalOption] = useState(null);
    const [amount, setAmount] = useState('');
    const [notes, setNotes] = useState('');
    const [showNotes, setShowNotes] = useState(false);
    const [tempNotes, setTempNotes] = useState('');
    const [cardSelection, setCardSelection] = useState({});
    const [apiCards, setApiCards] = useState({
        usedCards: [],
        availableCards: [],
    });

    function parseCardString(cardStr) {
        // Example: "AhKdKcQs"
        if (!cardStr || cardStr.length % 2 !== 0) return {};
        const result = {};
        for (let i = 0; i < cardStr.length; i += 2) {
            const idx = (i / 2) + 1;
            result[`value${idx}`] = cardStr[i];
            result[`type${idx}`] = cardStr[i + 1].toLowerCase();
        }
        return result;
    }

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`${API_ENDPOINTS.FLOP}?id=${id}`);
            setData(res.data);
            setCardSelection(parseCardString(res.data.flop));
            setNotes(res.data.notes)
        } catch (error) {
            toast.error("Failed to fetch flop data");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (actionsEndRef.current) {
            actionsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [data]);

    useEffect(() => {
        const fetchCards = async () => {
            try {
                const response = await axiosInstance.get(`${API_ENDPOINTS.AVILABELCARD}?id=${id}`);
                setApiCards({
                    usedCards: response.data.usedCards || [],
                    availableCards: response.data.availableCards || [],
                });
            } catch (error) {
                // Optionally handle error
                setApiCards({ usedCards: [], availableCards: [] });
            }
        };
        if (id) fetchCards();
    }, [id]);



    const handleAction = (option, index) => {
        if (option.action === 'raise' || option.action === 'shove' || option.action === 'bet') {
            setModalOption({ ...option, index });
            setAmount(option.amount || '');
            setShowModal(true);
        } else {
            sendAction(option.action, option.amount ?? "0", index);
        }
    };

    function cardSelectionToString(selection) {
        let result = '';
        for (let i = 1; i <= 3; i++) {
            if (selection[`value${i}`] && selection[`type${i}`]) {
                result += selection[`value${i}`] + selection[`type${i}`];
            }
        }
        return result;
    }

    const sendAction = (action, amt, index) => {
        const numericAmount = Number(amt);

        // Validation for Raise
        if (action === 'raise' || action === 'bet') {
            console.log(modalOption.min);
            if (modalOption?.min && numericAmount < modalOption.min) {
                toast.error(`${action.charAt(0).toUpperCase() + action.slice(1)} amount must be at least ${modalOption.min}`);
                return;
            }
        }

        setShowModal(false);
        setLoading(true);

        axiosInstance.patch(`${API_ENDPOINTS.FLOP}?id=${id}`, {
            flop: cardSelectionToString(cardSelection),
            notes: notes,
            event: {
                index,
                action,
                amount: String(numericAmount ?? "0"),
            }
        })
            .then((response) => {
                setData(response.data);  // ✅ Update state with PATCH response
            })
            .catch(() => {
                toast.error("Failed to send action");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // Render loading state
    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-[200px]">
                <ThreeDots
                    visible={true}
                    height="80"
                    width="80"
                    color="#1976d2"
                    radius="9"
                    ariaLabel="three-dots-loading"
                    wrapperStyle={{}}
                    wrapperClass="dots-loading additional-class"
                />
            </div>
        );
    }

    const allCardsSelected =
        cardSelection.value1 && cardSelection.type1 &&
        cardSelection.value2 && cardSelection.type2 &&
        cardSelection.value3 && cardSelection.type3;

    // No data fallback
    if (!data) return <div className="text-white">No data</div>;

    // Helper to render position card
    const renderPositionCard = (label, options, index, disabled = false) => (
        <div key={label} className="flex gap-3 items-center">
            <div className={` font-bold text-md md:text-lg w-12 md:w-20 tracking-wide ${label === data.position ? 'text-white' : 'text-[#1976d2]'}`}>{label}</div>
            <div className="flex flex-wrap gap-2 md:gap-3">
                {options.map(option => (
                    <button
                        key={option.action}
                        disabled={disabled || option.disabled}
                        onClick={() => handleAction(option, index)}
                        className={`px-3 py-2 rounded-lg border-2 font-semibold text-sm transition-all
                            ${disabled || option.disabled
                                ? 'border-gray-600 bg-gray-700 text-gray-400 cursor-not-allowed'
                                : 'border-[#1976d230] text-white hover:bg-[#1976d2] cursor-pointer'}
                            ${option.selected && 'bg-[#1976d230] border-[#1976d230]'}
                        `}
                    >
                        {option.label}
                        {option.amount && option.amount !== "0" ? ` ${option.amount}` : ''}
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <div className="text-white w-full px-3 py-6 md:p-6">
            {/* Header */}
            <div className="flex flex-wrap items-center mb-6">
                <button
                    className="border border-border hover:bg-gray-800 text-white text-xl mr-3 w-10 h-10 flex items-center justify-center rounded-lg"
                    onClick={() => navigate(-1)}
                    type="button"
                >
                    <IoChevronBackOutline />
                </button>
                <h1 className="font-bold text-xl m-0">Flop</h1>
                <button
                    className="bg-secondary ml-auto w-full md:w-auto  mt-5 md:mt-0 justify-center   hover:opacity-75 px-4 py-2 rounded text-white flex items-center gap-2  "
                    onClick={() => {
                        setShowNotes(prev => !prev);
                        setTempNotes(notes);
                    }}
                >
                    <GrNotes className='w-4 h-4' />
                    Notes
                </button>
            </div>

            {/* Main content */}
            <div className="flex flex-wrap justify-center w-full gap-8">
                <div className="bg-[#111] w-full rounded-xl flex flex-col items-start gap-6 shadow-lg p-6">
                    <CardSelector
                        count={3}
                        value={cardSelection}
                        onChange={setCardSelection}
                        usedCards={[
                            ...apiCards.usedCards,
                            ...(cardSelection.value1 && cardSelection.type1 ? [`${cardSelection.value1}${cardSelection.type1}`] : []),
                            ...(cardSelection.value2 && cardSelection.type2 ? [`${cardSelection.value2}${cardSelection.type2}`] : []),
                            ...(cardSelection.value3 && cardSelection.type3 ? [`${cardSelection.value3}${cardSelection.type3}`] : []),
                        ]}
                    />
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-10 w-full'>
                        {allCardsSelected &&
                            <div className='flex flex-col gap-6 flex-1'>
                                {data.current && data.current.map((item, idx) =>
                                    renderPositionCard(
                                        item.pos,
                                        item.options,
                                        idx,
                                        item.options.every(opt => opt.disabled)
                                    )
                                )}
                                {data.next && data.next.options && data.next.options.length > 0 &&
                                    renderPositionCard(
                                        data.next.label,
                                        data.next.options,
                                        data.next.index
                                    )
                                }
                                <div ref={actionsEndRef}></div>
                            </div>
                        }

                        <div className='flex-1'>
                            {showNotes && (
                                <div className=' fixed inset-0 z-[9999] flex items-center justify-center px-3 bg-black bg-opacity-60'>
                                    <div className='max-w-[600px] mx-auto w-full border border-border bg-[#181818] rounded shadow-lg p-6'>
                                        <div className='flex flex-col gap-3'>
                                            <label className="block mb-4 text-lg">Add Notes</label>
                                            <textarea
                                                className='w-full bg-primary border border-border rounded-lg p-2 min-h-[200px] text-white'
                                                placeholder='Write your notes...'
                                                onChange={e => setTempNotes(e.target.value)}
                                                value={tempNotes}
                                            ></textarea>
                                            <div className="flex justify-start flex-row-reverse gap-4 mt-2">
                                                <button
                                                    className="bg-secondary hover:opacity-80 px-4 py-2 rounded text-white"
                                                    onClick={async () => {
                                                        try {
                                                            setLoading(true);
                                                            const res = await axiosInstance.patch(`${API_ENDPOINTS.FLOP}?id=${id}`, {
                                                                notes: tempNotes
                                                            });
                                                            setNotes(res.data.notes);
                                                            toast.success("Notes updated");
                                                            setShowNotes(false);
                                                        } catch (err) {
                                                            toast.error("Failed to update notes");
                                                        } finally {
                                                            setLoading(false);
                                                        }
                                                    }}
                                                >
                                                    Save Note
                                                </button>
                                                <button
                                                    className="border border-white px-4 py-2 rounded text-white"
                                                    onClick={() => setShowNotes(false)}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* "Go to Flop" button */}
                    {(!data.next || !data.next.options || data.next.options.length === 0) && (
                        <div className='flex justify-end w-full'>
                            <button
                                className="bg-secondary  hover:opacity-75 px-4 py-2 rounded text-white flex items-center gap-2"
                                onClick={() => navigate(`/Sessions/SessionCard/${data.nextPage}/${id}`)}
                            >
                                Go to Turn
                            </button>
                        </div>
                    )}
                </div>


            </div>


            {/* Modal */}
            {showModal && modalOption && (
                <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[99999]">
                    <div className="bg-gray-800 p-8 rounded-xl shadow-2xl min-w-[340px] flex flex-col items-center">
                        <h2 className="text-[#1976d2] text-2xl font-bold mb-4">{modalOption.label} Amount</h2>
                        <input
                            type="number"
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                            className="p-3 text-lg rounded-md border-2 border-[#1976d2] bg-gray-900 text-white w-4/5 mb-6 focus:outline-none "
                            min="0"
                        />
                        <div className="flex gap-6">
                            <button
                                onClick={() => sendAction(modalOption.action, amount || "0", modalOption.index)}
                                className="bg-secondary  hover:opacity-75 px-4 py-2 rounded text-white flex items-center gap-2"
                            >
                                Save
                            </button>
                            <button
                                onClick={() => setShowModal(false)}
                                className="border-2 border-[#1976d230] text-white font-bold px-6 py-2 rounded-lg hover:bg-[#1976d2] transition-all"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Flop;
